import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  ArrowDownToLine,
  ArrowUpFromLine,
  Check,
  Loader2,
  Percent,
  ShieldCheck,
  WalletCards,
} from "lucide-react";

import { toast } from "sonner";

import { AdvertiserTier } from "@/interfaces/advertisement";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Switch } from "@/components/ui/switch";
import type {
  CreateTransactionSettingPayload,
  TransactionLimitType,
  TransactionTierSetting,
  UpdateTransactionSettingPayload,
} from "@/interfaces/transaction-tier-settings";
import {
  useCreateTransactionSetting,
  useUpdateTransactionSetting,
} from "@/hooks";

interface TransactionSettingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  setting?: TransactionTierSetting | null;
}

interface FormState {
  tier: AdvertiserTier;
  type: TransactionLimitType;
  enabled: boolean;

  minAmount: string;
  maxAmount: string;

  feePercent: string;
  feeCap: string;

  dailyLimit: string;
  weeklyLimit: string;
  monthlyLimit: string;
  yearlyLimit: string;
}

const EMPTY_FORM: FormState = {
  tier: AdvertiserTier.BASIC,
  type: "TRANSFER",
  enabled: true,

  minAmount: "",
  maxAmount: "",

  feePercent: "",
  feeCap: "",

  dailyLimit: "",
  weeklyLimit: "",
  monthlyLimit: "",
  yearlyLimit: "",
};

const tierOptions = [
  {
    value: AdvertiserTier.BASIC,
    label: "Basic",
  },
  {
    value: AdvertiserTier.VERIFIED,
    label: "Verified",
  },
  {
    value: AdvertiserTier.TRUSTED,
    label: "Trusted",
  },
  {
    value: AdvertiserTier.BUSINESS_BASIC,
    label: "Business Basic",
  },
  {
    value: AdvertiserTier.BUSINESS_VERIFIED,
    label: "Business Verified",
  },
  {
    value: AdvertiserTier.BUSINESS_ENTERPRISE,
    label: "Business Enterprise",
  },
];

const transactionTypes = [
  {
    value: "TRANSFER" as TransactionLimitType,
    label: "Transfer",
    description: "Internal or wallet-to-wallet transfers",
    icon: ArrowUpFromLine,
  },
  {
    value: "WITHDRAWAL" as TransactionLimitType,
    label: "Withdrawal",
    description: "Blockchain withdrawal transactions",
    icon: ArrowDownToLine,
  },
];

export function TransactionTierSettingDialog({
  open,
  onOpenChange,
  setting,
}: TransactionSettingDialogProps) {
  const isEditing = !!setting;
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const createMutation = useCreateTransactionSetting();
  const updateMutation = useUpdateTransactionSetting();
  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (setting) {
      setForm({
        tier: setting.tier,
        type: setting.type,
        enabled: setting.enabled,
        minAmount: String(setting.minAmount),
        maxAmount: String(setting.maxAmount),
        feePercent: String(setting.feePercent),
        feeCap: String(setting.feeCap),
        dailyLimit: String(setting.dailyLimit),
        weeklyLimit: String(setting.weeklyLimit),
        monthlyLimit: String(setting.monthlyLimit),
        yearlyLimit: String(setting.yearlyLimit),
      });
    } else {
      setForm(EMPTY_FORM);
    }

    setErrors({});
  }, [open, setting]);

  const updateField = <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => {
      const next = { ...previous };

      delete next[field];

      return next;
    });
  };

  const numericValues = useMemo(() => {
    return {
      minAmount: Number(form.minAmount),
      maxAmount: Number(form.maxAmount),
      feePercent: Number(form.feePercent),
      feeCap: Number(form.feeCap),
      dailyLimit: Number(form.dailyLimit),
      weeklyLimit: Number(form.weeklyLimit),
      monthlyLimit: Number(form.monthlyLimit),
      yearlyLimit: Number(form.yearlyLimit),
    };
  }, [form]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};

    const {
      minAmount,
      maxAmount,
      feePercent,
      feeCap,
      dailyLimit,
      weeklyLimit,
      monthlyLimit,
      yearlyLimit,
    } = numericValues;

    if (!form.tier) {
      nextErrors.tier = "Select an advertiser tier.";
    }

    if (!form.type) {
      nextErrors.type = "Select a transaction type.";
    }

    if (!Number.isFinite(minAmount) || minAmount <= 0) {
      nextErrors.minAmount = "Minimum amount must be greater than zero.";
    }

    if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
      nextErrors.maxAmount = "Maximum amount must be greater than zero.";
    }

    if (
      Number.isFinite(minAmount) &&
      Number.isFinite(maxAmount) &&
      maxAmount < minAmount
    ) {
      nextErrors.maxAmount =
        "Maximum amount cannot be less than minimum amount.";
    }

    if (!Number.isFinite(feePercent) || feePercent < 0 || feePercent > 100) {
      nextErrors.feePercent = "Fee percentage must be between 0 and 100.";
    }

    if (!Number.isFinite(feeCap) || feeCap <= 0) {
      nextErrors.feeCap = "Fee cap must be greater than zero.";
    }

    if (!Number.isFinite(dailyLimit) || dailyLimit <= 0) {
      nextErrors.dailyLimit = "Daily limit must be greater than zero.";
    }

    if (!Number.isFinite(weeklyLimit) || weeklyLimit <= 0) {
      nextErrors.weeklyLimit = "Weekly limit must be greater than zero.";
    }

    if (!Number.isFinite(monthlyLimit) || monthlyLimit <= 0) {
      nextErrors.monthlyLimit = "Monthly limit must be greater than zero.";
    }

    if (!Number.isFinite(yearlyLimit) || yearlyLimit <= 0) {
      nextErrors.yearlyLimit = "Yearly limit must be greater than zero.";
    }

    if (
      Number.isFinite(dailyLimit) &&
      Number.isFinite(weeklyLimit) &&
      dailyLimit > weeklyLimit
    ) {
      nextErrors.weeklyLimit = "Weekly limit cannot be less than daily limit.";
    }

    if (
      Number.isFinite(weeklyLimit) &&
      Number.isFinite(monthlyLimit) &&
      weeklyLimit > monthlyLimit
    ) {
      nextErrors.monthlyLimit =
        "Monthly limit cannot be less than weekly limit.";
    }

    if (
      Number.isFinite(monthlyLimit) &&
      Number.isFinite(yearlyLimit) &&
      monthlyLimit > yearlyLimit
    ) {
      nextErrors.yearlyLimit =
        "Yearly limit cannot be less than monthly limit.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please correct the highlighted fields.");

      return;
    }

    const payload = {
      tier: form.tier,
      type: form.type,
      enabled: form.enabled,
      minAmount: numericValues.minAmount,
      maxAmount: numericValues.maxAmount,
      feePercent: numericValues.feePercent,
      feeCap: numericValues.feeCap,
      dailyLimit: numericValues.dailyLimit,
      weeklyLimit: numericValues.weeklyLimit,
      monthlyLimit: numericValues.monthlyLimit,
      yearlyLimit: numericValues.yearlyLimit,
    };

    try {
      if (isEditing && setting) {
        const updatePayload: UpdateTransactionSettingPayload = {
          enabled: payload.enabled,
          minAmount: payload.minAmount,
          maxAmount: payload.maxAmount,
          feePercent: payload.feePercent,
          feeCap: payload.feeCap,
          dailyLimit: payload.dailyLimit,
          weeklyLimit: payload.weeklyLimit,
          monthlyLimit: payload.monthlyLimit,
          yearlyLimit: payload.yearlyLimit,
        };

        await updateMutation.mutateAsync({
          id: setting.id,
          payload: updatePayload,
        });
      } else {
        const createPayload: CreateTransactionSettingPayload = payload;

        await createMutation.mutateAsync(createPayload);
      }

      onOpenChange(false);
    } catch {
      // Mutation hooks already display server errors.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSubmitting) {
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent className="max-h-[92vh] w-[calc(100%-1rem)] max-w-3xl overflow-y-auto border-slate-800 bg-slate-950 p-0 text-white sm:w-full">
        <DialogHeader className="border-b border-slate-800 px-5 py-5 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-teal-500/20 bg-teal-500/10">
              <WalletCards className="h-5 w-5 text-teal-400" />
            </div>

            <div>
              <DialogTitle className="text-lg text-white">
                {isEditing
                  ? "Edit Transaction Policy"
                  : "Create Transaction Policy"}
              </DialogTitle>

              <DialogDescription className="mt-1 text-sm text-slate-500">
                Configure transaction limits and fee parameters for this
                advertiser tier.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 px-5 py-6 sm:px-6">
          {/* ==================================================
              POLICY
          ================================================== */}

          <section>
            <SectionHeading
              icon={<ShieldCheck className="h-4 w-4" />}
              title="Policy Configuration"
              description="Define who this policy applies to."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Advertiser Tier" required error={errors.tier}>
                <Select
                  value={form.tier}
                  onValueChange={(value) =>
                    updateField("tier", value as AdvertiserTier)
                  }
                >
                  <SelectTrigger className={fieldClass(!!errors.tier)}>
                    <SelectValue placeholder="Select tier" />
                  </SelectTrigger>

                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                    {tierOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormField>

              <FormField label="Transaction Type" required error={errors.type}>
                <Select
                  value={form.type}
                  onValueChange={(value) =>
                    updateField("type", value as TransactionLimitType)
                  }
                >
                  <SelectTrigger className={fieldClass(!!errors.type)}>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>

                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-200">
                    {transactionTypes.map((option) => {
                      const Icon = option.icon;

                      return (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-teal-400" />

                            <span>{option.label}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormField>
            </div>

            <div className="mt-4 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <div>
                <p className="text-sm font-medium text-slate-200">
                  Policy status
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Disabled policies cannot be used for new transactions.
                </p>
              </div>

              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => updateField("enabled", checked)}
              />
            </div>
          </section>

          {/* ==================================================
              TRANSACTION RANGE
          ================================================== */}

          <section>
            <SectionHeading
              icon={<WalletCards className="h-4 w-4" />}
              title="Transaction Range"
              description="Set the minimum and maximum USD-equivalent transaction amount."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AmountField
                label="Minimum Amount"
                value={form.minAmount}
                onChange={(value) => updateField("minAmount", value)}
                error={errors.minAmount}
                placeholder="0.00"
              />

              <AmountField
                label="Maximum Amount"
                value={form.maxAmount}
                onChange={(value) => updateField("maxAmount", value)}
                error={errors.maxAmount}
                placeholder="0.00"
              />
            </div>
          </section>

          {/* ==================================================
              FEES
          ================================================== */}

          <section>
            <SectionHeading
              icon={<Percent className="h-4 w-4" />}
              title="Fee Configuration"
              description="Configure the percentage fee and maximum fee charged."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AmountField
                label="Fee Percentage"
                value={form.feePercent}
                onChange={(value) => updateField("feePercent", value)}
                error={errors.feePercent}
                placeholder="0.00"
                suffix="%"
              />

              <AmountField
                label="Fee Cap"
                value={form.feeCap}
                onChange={(value) => updateField("feeCap", value)}
                error={errors.feeCap}
                placeholder="0.00"
                prefix="$"
              />
            </div>
          </section>

          {/* ==================================================
              LIMITS
          ================================================== */}

          <section>
            <SectionHeading
              icon={<ShieldCheck className="h-4 w-4" />}
              title="Usage Limits"
              description="Set cumulative USD-equivalent limits over each period."
            />

            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <AmountField
                label="Daily Limit"
                value={form.dailyLimit}
                onChange={(value) => updateField("dailyLimit", value)}
                error={errors.dailyLimit}
                placeholder="0.00"
                prefix="$"
              />

              <AmountField
                label="Weekly Limit"
                value={form.weeklyLimit}
                onChange={(value) => updateField("weeklyLimit", value)}
                error={errors.weeklyLimit}
                placeholder="0.00"
                prefix="$"
              />

              <AmountField
                label="Monthly Limit"
                value={form.monthlyLimit}
                onChange={(value) => updateField("monthlyLimit", value)}
                error={errors.monthlyLimit}
                placeholder="0.00"
                prefix="$"
              />

              <AmountField
                label="Yearly Limit"
                value={form.yearlyLimit}
                onChange={(value) => updateField("yearlyLimit", value)}
                error={errors.yearlyLimit}
                placeholder="0.00"
                prefix="$"
              />
            </div>
          </section>

          {/* ==================================================
              VALIDATION NOTICE
          ================================================== */}

          <div className="flex gap-3 rounded-xl border border-teal-500/10 bg-teal-500/5 p-4">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-teal-400" />

            <div className="text-xs leading-5 text-slate-400">
              <p className="font-medium text-slate-300">Policy hierarchy</p>

              <p className="mt-1">
                Daily limits must not exceed weekly limits. Weekly limits must
                not exceed monthly limits, and monthly limits must not exceed
                yearly limits.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-slate-800 bg-slate-950 px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
            className="w-full border-slate-700 bg-slate-900 text-slate-300 hover:bg-slate-800 hover:text-white sm:w-auto"
          >
            Cancel
          </Button>

          <Button
            type="button"
            disabled={isSubmitting}
            onClick={handleSubmit}
            className="w-full bg-teal-500 text-slate-950 hover:bg-teal-400 sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isEditing ? "Saving..." : "Creating..."}
              </>
            ) : (
              <>
                <Check className="mr-2 h-4 w-4" />

                {isEditing ? "Save Changes" : "Create Policy"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   SECTION HEADING
============================================================ */

function SectionHeading({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 text-teal-400">{icon}</div>

      <div>
        <h3 className="text-sm font-semibold text-slate-200">{title}</h3>

        <p className="mt-0.5 text-xs text-slate-500">{description}</p>
      </div>
    </div>
  );
}

/* ============================================================
   FORM FIELD
============================================================ */

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-400">
        {label}

        {required && <span className="ml-1 text-red-400">*</span>}
      </label>

      {children}

      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

/* ============================================================
   AMOUNT FIELD
============================================================ */

function AmountField({
  label,
  value,
  onChange,
  error,
  placeholder,
  prefix,
  suffix,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
}) {
  return (
    <FormField label={label} required error={error}>
      <div className="relative">
        {prefix && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            {prefix}
          </span>
        )}

        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          className={`${fieldClass(!!error)} ${prefix ? "pl-7" : ""} ${
            suffix ? "pr-8" : ""
          }`}
        />

        {suffix && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">
            {suffix}
          </span>
        )}
      </div>
    </FormField>
  );
}

/* ============================================================
   INPUT CLASS
============================================================ */

function fieldClass(hasError: boolean) {
  return [
    "h-10",
    "border-slate-700",
    "bg-slate-900",
    "text-slate-200",
    "placeholder:text-slate-600",
    "focus-visible:ring-teal-500/30",
    hasError ? "border-red-500/60 focus-visible:ring-red-500/20" : "",
  ].join(" ");
}
